import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from 'src/app/services/auth.service';

/**
 * Step 2 of the forgot-password flow: the code emailed by ForgotPassword,
 * plus the new password. Confirming both changes the password and (on
 * success) logs the user straight in — mirrors the mobile app's
 * OtpVerification screen.
 */
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  form: FormGroup;
  loading = false;
  resending = false;
  email = '';
  hidePassword = true;
  hideConfirm = true;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
  ) {
    this.form = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    }, { validators: this.passwordsMatch });
  }

  ngOnInit(): void {
    this.email = this.route.snapshot.queryParamMap.get('email') || '';
    if (!this.email) {
      // Nothing to confirm a code against — send them back to request one.
      this.router.navigate(['/forgot-password']);
    }
  }

  private passwordsMatch(group: FormGroup) {
    const password = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return password === confirm ? null : { mismatch: true };
  }

  async resendCode(): Promise<void> {
    if (this.resending) return;
    this.resending = true;
    try {
      await this.auth.requestPasswordReset(this.email);
    } catch {
      // AuthService already surfaces a toast on real failures.
    } finally {
      this.resending = false;
    }
  }

  async submit(): Promise<void> {
    if (!this.form.valid || this.loading) {
      return;
    }

    this.loading = true;
    try {
      const response = await this.auth.confirmPasswordReset(
        this.email,
        this.form.value.code,
        this.form.value.newPassword,
      );

      if (response?.token) {
        this.router.navigate(['/dashboard']);
      }
    } catch {
      // AuthService/GraphqlService already surface a toast on failure.
    } finally {
      this.loading = false;
    }
  }
}
